import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { authFetch } from '@/lib/api'
import { formatInteger, formatMoney } from '@/lib/format'
import type { AttachmentSuggestion } from '@/lib/staged-attachment'

import type { SearchResponse, SearchResult } from './types'

// Match the staged-chip entrance so the row reads as part of the same object
// landing in the composer. fade + slide-up, ease-out, ≤200ms, reduced-motion
// safe. A short stagger between chips is decorative — kept ≤60ms.
const enterMotion =
  'animate-in fade-in slide-in-from-bottom-2 duration-200 ease-out motion-reduce:animate-none'

export type AttachmentSuggestionHandlers = {
  /** "Add {veh} to {customer}" / orphan when customerId is null. */
  onAddVehicle: (s: Extract<AttachmentSuggestion, { kind: 'add_vehicle' }>) => void
  /** Add the decoded vehicle to a picked customer. */
  onAddVehicleForPickedCustomer: (
    s: Extract<AttachmentSuggestion, { kind: 'pick_customer' }>,
    customer: SearchResult,
  ) => void
  /** Save the decoded vehicle with no owner. */
  onSaveOrphan: (s: Extract<AttachmentSuggestion, { kind: 'save_orphan' }>) => void
  /** Log the read mileage on the focus vehicle. */
  onLogMileage: (s: Extract<AttachmentSuggestion, { kind: 'log_mileage' }>) => void
  /** Log the read mileage on a picked vehicle. */
  onLogMileageForPickedVehicle: (
    s: Extract<AttachmentSuggestion, { kind: 'pick_vehicle' }>,
    vehicle: SearchResult,
  ) => void
  /** Save the photo as a receipt on the customer. */
  onSaveReceipt: (s: Extract<AttachmentSuggestion, { kind: 'save_receipt' }>) => void
  /** Hand the staged photo to Eric (sends + aborts the classify). */
  onAskEric: () => void
}

/**
 * `<AttachmentSuggestions />` — the content-aware chip row that appears once a
 * staged photo is classified. Hierarchy is by WEIGHT + COLOR, never size: the
 * recommended action is one filled (`accent`) chip; alternatives are quieter
 * (`chip` / `ghost`). Built on the CascadeChip pattern
 * (`Button variant="chip" size="pill-chip"`, ThreadCascade.tsx:158) so it
 * matches the rest of the composer's pill vocabulary.
 *
 * "Pick customer" / "Pick vehicle" flip the row into an inline search picker
 * (generalized from the no-focus VIN picker) rather than dumping the user onto
 * a list page — the mechanic already opened the assistant.
 *
 * 44px touch targets (`pill-chip` is h-10), `truncate` on names so a long
 * business / vehicle label ellipsizes instead of overflowing at 375px, and
 * `flex-wrap` so the chips reflow on a narrow phone.
 */
export function AttachmentSuggestions({
  suggestions,
  handlers,
  busy,
}: {
  suggestions: AttachmentSuggestion[]
  handlers: AttachmentSuggestionHandlers
  /** True while an add/log/save mutation is in flight — disables the chips. */
  busy: boolean
}) {
  const { t } = useTranslation('dashboard')

  // Inline picker mode: which suggestion (if any) flipped the row into a
  // search picker, and for what entity kind.
  const [picking, setPicking] = useState<
    | { mode: 'customer'; src: Extract<AttachmentSuggestion, { kind: 'pick_customer' }> }
    | { mode: 'vehicle'; src: Extract<AttachmentSuggestion, { kind: 'pick_vehicle' }> }
    | null
  >(null)

  // A fresh suggestion set (a new photo) cancels any open picker. Adjusted
  // synchronously during render off an identity check (prev-snapshot in state)
  // — not via a useEffect, which the repo's react-hooks/set-state-in-effect
  // lint rule rejects (hard CI gate). Mirrors the prevDispatchResultForUndo
  // pattern in TaskEricInput.
  const [prevSuggestions, setPrevSuggestions] = useState(suggestions)
  if (prevSuggestions !== suggestions) {
    setPrevSuggestions(suggestions)
    if (picking !== null) setPicking(null)
  }

  if (picking) {
    return (
      <InlineEntityPicker
        mode={picking.mode}
        busy={busy}
        onCancel={() => setPicking(null)}
        onPick={(result) => {
          if (picking.mode === 'customer' && result.kind === 'customer') {
            handlers.onAddVehicleForPickedCustomer(picking.src, result)
          } else if (picking.mode === 'vehicle' && result.kind === 'vehicle') {
            handlers.onLogMileageForPickedVehicle(picking.src, result)
          }
          setPicking(null)
        }}
      />
    )
  }

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${enterMotion}`}>
      {suggestions.map((s, i) => {
        // Decorative stagger — ≤60ms between chips, capped so a long row
        // doesn't feel slow. Reduced-motion zeroes the whole entrance above.
        const style = { animationDelay: `${Math.min(i, 4) * 40}ms` }

        if (s.kind === 'add_vehicle') {
          const label = s.customerName
            ? t('agentic.attach.chip.add_vehicle_to', {
                vehicle: s.vehicleLabel,
                name: s.customerName,
              })
            : t('agentic.attach.chip.add_vehicle_orphan', {
                vehicle: s.vehicleLabel,
              })
          return (
            <ActionChip
              key={`add_vehicle-${i}`}
              style={style}
              tone={s.tone}
              busy={busy}
              onClick={() => handlers.onAddVehicle(s)}
              label={label}
            />
          )
        }
        if (s.kind === 'pick_customer') {
          return (
            <ActionChip
              key={`pick_customer-${i}`}
              style={style}
              tone={s.tone}
              busy={busy}
              onClick={() => setPicking({ mode: 'customer', src: s })}
              label={t('agentic.attach.chip.pick_customer')}
            />
          )
        }
        if (s.kind === 'save_orphan') {
          return (
            <ActionChip
              key={`save_orphan-${i}`}
              style={style}
              tone="quiet"
              busy={busy}
              onClick={() => handlers.onSaveOrphan(s)}
              label={t('agentic.attach.chip.save_orphan')}
            />
          )
        }
        if (s.kind === 'log_mileage') {
          const label = s.vehicleLabel
            ? t('agentic.attach.chip.log_mileage_on', {
                km: formatInteger(s.mileageKm),
                vehicle: s.vehicleLabel,
              })
            : t('agentic.attach.chip.log_mileage', {
                km: formatInteger(s.mileageKm),
              })
          return (
            <ActionChip
              key={`log_mileage-${i}`}
              style={style}
              tone={s.tone}
              busy={busy}
              onClick={() => handlers.onLogMileage(s)}
              label={label}
            />
          )
        }
        if (s.kind === 'pick_vehicle') {
          return (
            <ActionChip
              key={`pick_vehicle-${i}`}
              style={style}
              tone={s.tone}
              busy={busy}
              onClick={() => setPicking({ mode: 'vehicle', src: s })}
              label={t('agentic.attach.chip.log_mileage_pick', {
                km: formatInteger(s.mileageKm),
              })}
            />
          )
        }
        if (s.kind === 'save_receipt') {
          const total =
            s.totalCents !== null
              ? formatMoney(s.totalCents / 100, {
                  currency: s.currency ?? 'CAD',
                })
              : null
          const label = s.customerName
            ? total
              ? t('agentic.attach.chip.save_receipt_total', {
                  total,
                  name: s.customerName,
                })
              : t('agentic.attach.chip.save_receipt', { name: s.customerName })
            : t('agentic.attach.chip.save_receipt_generic')
          return (
            <ActionChip
              key={`save_receipt-${i}`}
              style={style}
              tone={s.tone}
              busy={busy}
              onClick={() => handlers.onSaveReceipt(s)}
              label={label}
            />
          )
        }
        // ask_eric
        return (
          <ActionChip
            key={`ask_eric-${i}`}
            style={style}
            tone={s.tone}
            busy={busy}
            onClick={handlers.onAskEric}
            label={t('agentic.attach.chip.ask_eric')}
            icon={<Sparkles className="size-4" aria-hidden />}
          />
        )
      })}
    </div>
  )
}

/**
 * One action chip. `tone` maps to visual weight: `primary` = filled accent
 * (the recommended action), `quiet` = the standard outlined chip. 44px touch
 * target via `pill-chip` (h-10); the label truncates with a max width so a
 * long name ellipsizes instead of overflowing the composer at 375px.
 */
function ActionChip({
  label,
  onClick,
  tone,
  busy,
  style,
  icon,
}: {
  label: string
  onClick: () => void
  tone: 'primary' | 'quiet'
  busy: boolean
  style?: React.CSSProperties
  icon?: React.ReactNode
}) {
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={busy}
      variant={tone === 'primary' ? 'accent' : 'chip'}
      size="pill-chip"
      style={style}
      className={`max-w-full text-sm font-medium ${enterMotion} ${
        tone === 'primary' ? '' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}
      <span className="truncate">{label}</span>
    </Button>
  )
}

/**
 * Inline customer/vehicle search picker — generalized from the no-focus VIN
 * picker. Same `/api/search` endpoint, debounced, 44px result rows, "Back" to
 * the chip row. Keeps the mechanic in the assistant instead of routing to a
 * list page.
 */
function InlineEntityPicker({
  mode,
  busy,
  onPick,
  onCancel,
}: {
  mode: 'customer' | 'vehicle'
  busy: boolean
  onPick: (result: SearchResult) => void
  onCancel: () => void
}) {
  const { t } = useTranslation('dashboard')
  const { getToken } = useAuth()
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 150)
    return () => clearTimeout(h)
  }, [q])

  const search = useQuery({
    queryKey: ['agentic-attach-picker', mode, debounced],
    enabled: debounced.length >= 2,
    queryFn: () =>
      authFetch<SearchResponse>(
        `/api/search?q=${encodeURIComponent(debounced)}&kinds=${mode}&limit=6`,
        getToken,
      ),
    staleTime: 10_000,
  })

  const results = (search.data?.results ?? []).filter((r) => r.kind === mode)

  return (
    <div className={enterMotion}>
      <input
        type="text"
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={
          mode === 'customer'
            ? t('agentic.attach.pick_customer_placeholder')
            : t('agentic.attach.pick_vehicle_placeholder')
        }
        aria-label={
          mode === 'customer'
            ? t('agentic.attach.pick_customer_placeholder')
            : t('agentic.attach.pick_vehicle_placeholder')
        }
        className="min-h-11 w-full rounded-md border border-border bg-background px-2.5 text-base text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring md:text-sm"
      />
      {debounced.length < 2 ? null : search.isLoading ? (
        <div className="mt-1.5 text-xs text-muted-foreground">
          {t('agentic.attach.pick_customer_loading')}
        </div>
      ) : results.length === 0 ? (
        <div className="mt-1.5 text-xs text-muted-foreground">
          {mode === 'customer'
            ? t('agentic.attach.pick_customer_empty')
            : t('agentic.attach.pick_vehicle_empty')}
        </div>
      ) : (
        <div className="mt-1.5 flex flex-col gap-1">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={busy}
              onClick={() => onPick(r)}
              className="flex min-h-11 flex-col justify-center rounded-md border border-border bg-background px-3 py-1 text-left transition hover:border-foreground/30 hover:bg-muted active:scale-[0.99] disabled:opacity-50"
            >
              <span className="truncate text-sm font-medium text-foreground">
                {r.name}
              </span>
              {r.meta ? (
                <span className="truncate text-xs text-muted-foreground">
                  {r.meta}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="mt-2 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {t('agentic.attach.pick_customer_back')}
      </Button>
    </div>
  )
}
